import { fetchWithChatFeedback } from '../api/chatFeedbackRequired';
import { Select } from './FarChat/ChatFeedbackOverlays';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControl, MenuItem, TextField, Typography } from '@mui/material';
import { resolveCountyLocation, type LocationInfo } from '../api/wageDetFunctions';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const LOOKUP_DELAY_MS = 350;
const fieldCaptionSx = { display: 'block', mb: 0.5, color: 'text.primary', fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35 } as const;

export type ConstructionProjectLocation = Record<string, string | null | undefined>;

interface Props {
  location: ConstructionProjectLocation;
  onLocationChange: (location: ConstructionProjectLocation) => void;
  onBlur: () => void;
  canEdit: boolean;
  /** Injected only by fixture tests; production loads the same county map as Wage Determination. */
  countyMap?: Record<string, string[]>;
}

type LookupState = 'idle' | 'loading' | 'no_match' | 'error' | 'candidate';

const normalizedState = (value: string | null | undefined) => String(value || '').trim().toUpperCase();

const ConstructionLocationPicker: React.FC<Props> = ({ location, onLocationChange, onBlur, canEdit, countyMap: suppliedMap }) => {
  const [loadedMap, setLoadedMap] = useState<Record<string, string[]>>({});
  const [mapError, setMapError] = useState(false);
  const [query, setQuery] = useState('');
  const [lookupState, setLookupState] = useState<LookupState>('idle');
  const [candidate, setCandidate] = useState<LocationInfo | null>(null);
  const requestSequence = useRef(0);
  const countyMap = suppliedMap ?? loadedMap;
  const state = normalizedState(location.state);
  const counties = useMemo(() => countyMap[state] ?? [], [countyMap, state]);
  const states = useMemo(() => {
    const values = Object.keys(countyMap).sort();
    return state && !values.includes(state) ? [state, ...values] : values;
  }, [countyMap, state]);

  useEffect(() => {
    if (suppliedMap) return;
    let active = true;
    void fetchWithChatFeedback(`${API_BASE_URL}/api/data/counties`, { credentials: 'include' })
      .then(async (response) => {
        if (!response.ok) throw new Error('county map unavailable');
        return response.json();
      })
      .then((value) => {
        if (active && value && typeof value === 'object') setLoadedMap(value as Record<string, string[]>);
      })
      .catch(() => { if (active) setMapError(true); });
    return () => { active = false; };
  }, [suppliedMap]);

  useEffect(() => {
    const trimmed = query.trim();
    const sequence = ++requestSequence.current;
    setCandidate(null);
    if (!trimmed) {
      setLookupState('idle');
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setLookupState('loading');
      const lookup = state
        ? resolveCountyLocation(trimmed, state)
        : resolveCountyLocation(trimmed);
      void lookup.then((result) => {
        if (sequence !== requestSequence.current) return;
        if (!result?.state_abbrev || !result.county) {
          setLookupState('no_match');
          return;
        }
        setCandidate(result);
        setLookupState('candidate');
      }).catch(() => {
        if (sequence === requestSequence.current) setLookupState('error');
      });
    }, LOOKUP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [query, state]);

  const replaceLocation = (next: Partial<ConstructionProjectLocation>) => {
    // Country is retained exactly as persisted for legacy compatibility, but
    // Price Estimator no longer renders or edits it as a Contract Details fact.
    onLocationChange({ ...location, ...next });
    // A Select can blur before its selection handler runs. Queue the normal
    // Contract Details blur-save after the parent has synchronously updated
    // its draft ref, so the selected canonical state/county is not stranded
    // until the user happens to touch another field.
    void Promise.resolve().then(onBlur);
  };
  const chooseCandidate = () => {
    if (!candidate?.state_abbrev || !candidate.county) return;
    replaceLocation({
      state: candidate.state_abbrev.toUpperCase(), county: candidate.county,
      ...(candidate.city ? { city: candidate.city } : {}), county_fips: undefined,
    });
    setCandidate(null);
    setLookupState('idle');
    setQuery('');
  };

  return (
    <Box onBlur={onBlur}>
      <Typography variant="h6" component="h3" sx={{ mb: 0.5 }}>Project location</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Search a city, ZIP code, or address, then choose the returned state and county. You can also select them manually.
      </Typography>
      <Box>
        <Box component="label" htmlFor="price-location-search" sx={fieldCaptionSx}>Search location</Box>
        <TextField
          id="price-location-search" name="price-location-search" value={query} disabled={!canEdit} fullWidth size="small"
          onChange={(event) => setQuery(event.target.value)} inputProps={{ 'aria-label': 'Search location' }}
          helperText="Search does not change project facts until you choose a result."
          InputProps={{ endAdornment: lookupState === 'loading' ? <CircularProgress size={18} aria-label="Searching location" /> : undefined }}
        />
      </Box>
      {lookupState === 'candidate' && candidate && (
        <Alert severity="info" sx={{ mt: 1 }} action={<Button color="inherit" size="small" onClick={chooseCandidate}>Use {candidate.county} County, {candidate.state_abbrev}</Button>}>
          Select the resolved location to update project facts.
        </Alert>
      )}
      {lookupState === 'no_match' && <Alert severity="info" sx={{ mt: 1 }}>No matching state and county were found. Try a city, ZIP code, or full address.</Alert>}
      {lookupState === 'error' && <Alert severity="warning" sx={{ mt: 1 }}>Location search is unavailable. You can select a state and county manually.</Alert>}
      {mapError && <Alert severity="warning" sx={{ mt: 1 }}>County choices could not be loaded. Try location search again later.</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box component="span" id="price-location-state-caption" sx={fieldCaptionSx}>State <Box component="span" aria-hidden="true" sx={{ color: 'error.main' }}>*</Box></Box>
          <FormControl required fullWidth size="small" disabled={!canEdit}>
            <Select id="price-location-state" name="price-location-state" value={state} required SelectDisplayProps={{ 'aria-labelledby': 'price-location-state-caption', 'aria-required': true }} inputProps={{ name: 'price-location-state' }} onChange={(event) => replaceLocation({ state: event.target.value, county: '', county_fips: undefined })}>
              <MenuItem value=""><em>Select a state</em></MenuItem>
              {states.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box component="span" id="price-location-county-caption" sx={fieldCaptionSx}>County <Box component="span" aria-hidden="true" sx={{ color: 'error.main' }}>*</Box></Box>
          <FormControl required fullWidth size="small" disabled={!canEdit || !state}>
            <Select id="price-location-county" name="price-location-county" value={location.county ?? ''} required SelectDisplayProps={{ 'aria-labelledby': 'price-location-county-caption', 'aria-required': true }} inputProps={{ name: 'price-location-county' }} onChange={(event) => replaceLocation({ county: event.target.value, county_fips: undefined })}>
              <MenuItem value=""><em>Select a county</em></MenuItem>
              {location.county && !counties.includes(location.county) && <MenuItem value={location.county}>{location.county}</MenuItem>}
              {counties.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ConstructionLocationPicker;
